import type { MonitoringNotification } from "@/hooks/use-notifications";
import { useTranslations } from "@/hooks/use-translations";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
const money = (v: string) => {
	try {
		const n = BigInt(v);
		return `${n / 100n}.${(n % 100n).toString().padStart(2, "0")}`;
	} catch {
		return v;
	}
};
// Advisory notes the user can act on; other skip reasons are internal and stay out of the card.
const KNOWN_NOTES = new Set(["missing_budget", "currency_or_level_rule", "stale_statistics", "budget_changed"]);
const reviewStatusTone: Record<string, BadgeProps["tone"]> = {
	open: "warning",
	approved: "success",
	resolved: "success",
	rejected: "neutral",
	obsolete: "neutral",
};
export function NotificationItem({
	item,
	pending,
	onRead,
	onAnswer,
	onDiscuss,
	onRecheck,
}: {
	item: MonitoringNotification;
	pending: boolean;
	onRead: () => void;
	onAnswer: (action: "approve" | "reject") => void;
	onDiscuss: () => void;
	onRecheck: () => void;
}) {
	const t = useTranslations("notifications"),
		p = item.payload;
	// A change Meta never confirmed: look in Ads Manager, or ask Meta again. Later: released.
	if (p.kind === "unconfirmed") {
		const released = p.event === "released";
		return (
			<article className="p-4 border-b border-border-subtle text-text-primary text-sm">
				<div className="flex justify-between gap-3">
					<strong>
						{t(`unconfirmed.${released ? "released" : "opened"}`)} · {t(`unconfirmed.actions.${p.action}`)}
					</strong>
					{!item.read_at && (
						<button onClick={onRead} className="text-text-link underline underline-offset-2">
							{t("read")}
						</button>
					)}
				</div>
				<p className="mt-2 text-text-secondary">
					{t(p.level)} {p.metaId}
				</p>
				<p className="mt-1">
					{t(`unconfirmed.${released ? (p.outcome ?? "applied") : (p.reason ?? "no_answer")}`)}
				</p>
				{!released && p.executionId && (
					<div className="mt-3">
						<Button variant="secondary" size="sm" disabled={pending} onClick={onRecheck}>
							{t("unconfirmed.recheck")}
						</Button>
					</div>
				)}
			</article>
		);
	}
	// Meta is holding the object; nothing happens to it until Meta reports it ACTIVE again.
	if (p.kind === "status") {
		return (
			<article className="p-4 border-b border-border-subtle text-text-primary text-sm">
				<div className="flex justify-between gap-3">
					<strong>
						{t("held.title")} · {p.effectiveStatus}
					</strong>
					{!item.read_at && (
						<button onClick={onRead} className="text-text-link underline underline-offset-2">
							{t("read")}
						</button>
					)}
				</div>
				<p className="mt-2 text-text-secondary">
					{t(p.level === "campaign" ? "held.campaign" : p.level)} {p.metaId}:{" "}
					{t(`held.statuses.${p.effectiveStatus}`)}
				</p>
				<p className="text-text-tertiary">{t("held.note")}</p>
			</article>
		);
	}
	// An incident is about the pipeline, not an ad: a stalled collection, a dead worker, a job out of retries.
	if (p.kind === "incident") {
		return (
			<article className="p-4 border-b border-border-subtle text-text-primary text-sm">
				<div className="flex justify-between gap-3">
					<strong>{t(`incident.${p.event === "resolved" ? "resolved" : "opened"}`)}</strong>
					{!item.read_at && (
						<button onClick={onRead} className="text-text-link underline underline-offset-2">
							{t("read")}
						</button>
					)}
				</div>
				<p className="mt-2 text-text-secondary">
					{p.audience === "admin"
						? t(`incident.kinds.${p.incidentKind ?? "unknown"}`)
						: t(`incident.${p.event === "resolved" ? "userResolved" : "userOpened"}`)}
				</p>
			</article>
		);
	}
	// A generated creative: what happened to it, and the chat where its buttons are.
	if (p.kind === "creative") {
		return (
			<article className="p-4 border-b border-border-subtle text-text-primary text-sm">
				<div className="flex justify-between gap-3">
					<strong>{t(`creative.${p.event ?? "ready"}.${p.mediaType === "video" ? "video" : "image"}`)}</strong>
					{!item.read_at && (
						<button onClick={onRead} className="text-text-link underline underline-offset-2">
							{t("read")}
						</button>
					)}
				</div>
				{p.reason && <p className="mt-2 text-text-secondary">{p.reason}</p>}
				{p.conversationId && (
					<button onClick={onDiscuss} className="mt-3 text-text-link underline underline-offset-2">
						{t("creative.open")}
					</button>
				)}
			</article>
		);
	}
	// Super-admins only: creative generation fails for everyone until they fix the cause.
	if (p.kind === "creative-outage") {
		return (
			<article className="p-4 border-b border-border-subtle text-text-primary text-sm">
				<div className="flex justify-between gap-3">
					<strong>{t("creativeOutage.title")}</strong>
					{!item.read_at && (
						<button onClick={onRead} className="text-text-link underline underline-offset-2">
							{t("read")}
						</button>
					)}
				</div>
				<p className="mt-2 text-text-secondary">{t("creativeOutage.code", { code: p.errorCode ?? "" })}</p>
				{p.openedAt && <p className="text-text-tertiary">{new Date(p.openedAt).toLocaleString()}</p>}
			</article>
		);
	}
	// A connection event concerns the team, not one ad: no metrics, no review, nothing to discuss.
	if (p.kind === "connection") {
		return (
			<article className="p-4 border-b border-border-subtle text-text-primary text-sm">
				<div className="flex justify-between gap-3">
					<strong>{t("connection.title")}</strong>
					{!item.read_at && (
						<button onClick={onRead} className="text-text-link underline underline-offset-2">
							{t("read")}
						</button>
					)}
				</div>
				<p className="mt-2 text-text-secondary">{t(`connection.${p.event ?? "changed"}`)}</p>
				{p.event === "detached" && (
					<p className="text-text-tertiary">
						{t("connection.reason")}: {t(`connection.reasons.${p.reason ?? "disconnected"}`)} ·{" "}
						{t("connection.suspended")}: {p.monitorsSuspended ?? 0}
					</p>
				)}
				{p.event === "tracked_accounts_missing" && p.missingAccounts?.length ? (
					<p className="text-text-tertiary">{p.missingAccounts.join(", ")}</p>
				) : null}
			</article>
		);
	}
	return (
		<article className="p-4 border-b border-border-subtle text-text-primary text-sm">
			<div className="flex justify-between gap-3">
				<strong>
					{t(`actions.${p.action}`)} · {t(p.kind)}
				</strong>
				{!item.read_at && (
					<button onClick={onRead} className="text-text-link underline underline-offset-2">
						{t("read")}
					</button>
				)}
			</div>
			<p className="mt-2 text-text-secondary">
				{t(p.level)} {p.metaId}
			</p>
			<p className="text-text-tertiary">
				{p.period?.since} — {p.period?.until}
			</p>
			<p className="mt-1">{t(`reasons.${p.reasonCode}`)}</p>
			{p.note && KNOWN_NOTES.has(p.note) && (
				<p className="mt-1 text-text-secondary">{t(`notes.${p.note}`)}</p>
			)}
			{p.before && p.after && (
				<p>
					{money(p.before)} → {money(p.after)} {p.currency}
				</p>
			)}
			<dl className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2">
				{Object.entries(p.metrics)
					.filter(([, v]) => v !== null)
					.slice(0, 15)
					.map(([key, value]) => (
						<div key={key} className="flex justify-between gap-2">
							<dt className="text-text-tertiary">{key}</dt>
							<dd>{value}</dd>
						</div>
					))}
			</dl>
			{item.review_id && (
				<p className="mt-2">
					<Badge tone={reviewStatusTone[item.review_status ?? "open"] ?? "neutral"}>
						{t(`status.${item.review_status ?? "open"}`)}
					</Badge>
				</p>
			)}
			<div className="flex items-center gap-3 mt-3 flex-wrap">
				{item.review_id && item.review_status === "open" && (
					<>
						{["PAUSE", "INCREASE"].includes(p.action) && (
							<Button
								size="sm"
								disabled={pending}
								onClick={() => onAnswer("approve")}
							>
								{t("approve")}
							</Button>
						)}
						<Button
							variant="secondary"
							size="sm"
							disabled={pending}
							onClick={() => onAnswer("reject")}
						>
							{t("reject")}
						</Button>
					</>
				)}
				<button onClick={onDiscuss} className="text-text-link underline underline-offset-2">
					{t("discuss")}
				</button>
			</div>
		</article>
	);
}
